import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { getCardBackground, getPrimaryText, getSecondaryText, getLightBorder } from '../../utils/themeUtils';
import { safeGetItem } from "@mediatechgroup/app-version";
import { Button } from "../ui";

export interface OnboardingStep {
  target: string; // CSS selector for the element to highlight
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  highlightPadding?: number; // Extra padding around highlighted element
}

interface OnboardingWalkthroughProps {
  steps: OnboardingStep[];
  onComplete?: () => void;
  onDismiss?: () => void;
  storageKey: string; // Unique key for localStorage to track completion
  autoStart?: boolean; // Whether to start automatically
  isActive?: boolean; // External control of active state
}

/**
 * Resolve a step target to the element the user can actually SEE.
 *
 * Pages routinely emit the same `data-onboarding` anchor twice — once in a
 * `hidden lg:flex` desktop block and once in an `lg:hidden` mobile block.
 * Both are in the DOM, so `querySelector` returns whichever comes first in
 * source order; at the other breakpoint that one is `display:none` and its
 * rect is 0x0, which collapsed the highlight to a dot in the top-left corner.
 * Picking the first VISIBLE match makes the walkthrough breakpoint-correct
 * without every page having to dedupe its anchors.
 */
const resolveTarget = (selector: string): HTMLElement | null => {
  const matches = Array.from(
    document.querySelectorAll<HTMLElement>(selector)
  );
  const visible = matches.find((el) => {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
  return visible ?? null;
};

const OnboardingWalkthrough: React.FC<OnboardingWalkthroughProps> = ({
  steps,
  onComplete,
  onDismiss,
  storageKey,
  autoStart = true,
  isActive: externalIsActive,
}) => {
  const { isDarkMode } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  // null => the target isn't on screen; we drop the spotlight and center the
  // tooltip rather than stranding the old step's highlight (see updatePositions).
  const [highlightPosition, setHighlightPosition] = useState<
    { top: number; left: number; width: number; height: number } | null
  >(null);
  // Brand colors via --color-primary CSS var. See CLAUDE.md.
  const tooltipRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);

  // Step fields are read as primitives so the effects below don't re-fire on
  // every render: pages build `steps` inline, so the array identity is new
  // each time even though its contents never change.
  const stepCount = steps.length;
  const step = steps[currentStep];
  const targetSelector = step?.target;
  const placement = step?.placement ?? 'bottom';
  const highlightPadding = step?.highlightPadding ?? 8;

  useEffect(() => {
    // Check if onboarding has been completed/dismissed
    const completed = safeGetItem(storageKey);
    if (!completed && autoStart && stepCount > 0) {
      setIsActive(true);
    }
  }, [storageKey, autoStart, stepCount]);

  // Handle external isActive prop
  useEffect(() => {
    if (externalIsActive !== undefined) {
      setIsActive(externalIsActive);
    }
  }, [externalIsActive]);

  // Restart from step 1 each time the tour is (re)opened — otherwise replaying
  // it after finishing drops the user straight back on the final step.
  useEffect(() => {
    if (isActive) {
      setCurrentStep(0);
    }
  }, [isActive]);

  const updatePositions = useCallback(() => {
    if (!targetSelector) return;

    const targetElement = resolveTarget(targetSelector);
    if (!targetElement) {
      // Target isn't rendered at this breakpoint / in this state. Clear the
      // spotlight so we don't leave the PREVIOUS step's highlight sitting
      // under the new step's text, and fall back to a centered tooltip.
      setHighlightPosition(null);
      if (tooltipRef.current) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        setTooltipPosition({
          top: window.innerHeight / 2 - tooltipRect.height / 2,
          left: window.innerWidth / 2 - tooltipRect.width / 2,
        });
      }
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const padding = highlightPadding;

    // Update highlight position - use viewport coordinates since position is fixed
    setHighlightPosition({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });

    // Calculate tooltip position based on placement
    let tooltipTop = 0;
    let tooltipLeft = 0;

    if (tooltipRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      switch (placement) {
        case 'top':
          tooltipTop = rect.top - tooltipRect.height - 16;
          tooltipLeft = rect.left + rect.width / 2 - tooltipRect.width / 2;
          break;
        case 'bottom':
          tooltipTop = rect.bottom + 16;
          tooltipLeft = rect.left + rect.width / 2 - tooltipRect.width / 2;
          break;
        case 'left':
          tooltipTop = rect.top + rect.height / 2 - tooltipRect.height / 2;
          tooltipLeft = rect.left - tooltipRect.width - 16;
          break;
        case 'right':
          tooltipTop = rect.top + rect.height / 2 - tooltipRect.height / 2;
          tooltipLeft = rect.right + 16;
          break;
        case 'center':
          tooltipTop = window.innerHeight / 2 - tooltipRect.height / 2;
          tooltipLeft = window.innerWidth / 2 - tooltipRect.width / 2;
          break;
      }

      // Keep tooltip within viewport
      const maxLeft = window.innerWidth - tooltipRect.width - 16;
      const maxTop = window.innerHeight - tooltipRect.height - 16;
      tooltipLeft = Math.max(16, Math.min(tooltipLeft, maxLeft));
      tooltipTop = Math.max(16, Math.min(tooltipTop, maxTop));

      setTooltipPosition({ top: tooltipTop, left: tooltipLeft });
    }
  }, [targetSelector, placement, highlightPadding]);

  useEffect(() => {
    if (!isActive || !targetSelector) return;

    updatePositions();

    const handleScroll = () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(updatePositions);
    };

    window.addEventListener('resize', updatePositions);
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      window.removeEventListener('resize', updatePositions);
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isActive, targetSelector, updatePositions]);

  // Bring the target into view when the step changes. Without this, any step
  // anchored below the fold (save buttons, tables) spotlighted a region the
  // user couldn't see while the tooltip sat clamped against the viewport edge
  // pointing at nothing. Keyed on the step only, so it never fights the user's
  // own scrolling mid-step.
  useEffect(() => {
    if (!isActive || !targetSelector) return;
    resolveTarget(targetSelector)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }, [isActive, targetSelector]);

  const handleComplete = useCallback(() => {
    // Call hook callback FIRST (before unmounting)
    if (onComplete) {
      onComplete();
    }
    // Then hide the walkthrough
    setIsActive(false);
  }, [onComplete]);

  const handleDismiss = useCallback(() => {
    // Call hook callback FIRST (before unmounting)
    if (onDismiss) {
      onDismiss();
    }
    // Then hide the walkthrough
    setIsActive(false);
  }, [onDismiss]);

  const handleNext = useCallback(() => {
    if (currentStep < stepCount - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  }, [currentStep, stepCount, handleComplete]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleSkip = handleComplete;

  // Keyboard control. The overlay covers the page behind a full-screen scrim,
  // so Escape has to get the user out — before this, a keyboard-only user had
  // no way to leave the tour.
  useEffect(() => {
    if (!isActive) return;

    const onKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          handleDismiss();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNext();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePrevious();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isActive, handleDismiss, handleNext, handlePrevious]);

  // Move focus to the tooltip on each step so screen readers announce the new
  // title/content instead of leaving focus stranded on the page behind.
  useEffect(() => {
    if (isActive) {
      tooltipRef.current?.focus();
    }
  }, [isActive, currentStep]);

  if (!isActive || stepCount === 0 || !step) {
    return null;
  }

  return (
    <>
      {/* Overlay. data-onboarding-overlay is a global signal other
          login-time modals (e.g. What's New) check to avoid stacking. */}
      <div
        data-onboarding-overlay="true"
        className="fixed inset-0"
        style={{
          backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
          zIndex: 99998,
        }}
        onClick={handleDismiss}
      />

      {/* Highlight cutout. Omitted when the step's target isn't on screen —
          drawing it anyway would spotlight a stale or empty region. */}
      {highlightPosition && (
        <div
          data-testid="onboarding-highlight"
          className="fixed pointer-events-none"
          style={{
            top: `${highlightPosition.top}px`,
            left: `${highlightPosition.left}px`,
            width: `${highlightPosition.width}px`,
            height: `${highlightPosition.height}px`,
            boxShadow: `0 0 0 9999px ${isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)'}`,
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            zIndex: 99999,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-step-title"
        aria-describedby="onboarding-step-content"
        tabIndex={-1}
        className={`fixed ${getCardBackground(isDarkMode)} border ${getLightBorder(isDarkMode)} rounded-lg shadow-2xl max-w-md w-full mx-4 focus:outline-none`}
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          transition: 'all 0.3s ease',
          zIndex: 100000,
        }}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <h3
              id="onboarding-step-title"
              className={`text-lg font-semibold ${getPrimaryText(isDarkMode)}`}
            >
              {step.title}
            </h3>
            <Button
              type="button"
              variant="neutral"
              size="sm"
              iconOnly
              onClick={handleDismiss}
              aria-label="Close walkthrough"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>

          {/* Content */}
          <p
            id="onboarding-step-content"
            className={`mb-6 ${getSecondaryText(isDarkMode)} text-sm leading-relaxed`}
          >
            {step.content}
          </p>

          {/* Progress indicator */}
          <div className="flex items-center gap-1 mb-6">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  index === currentStep
                    ? "bg-[var(--cc-action)]"
                    : index < currentStep
                      ? isDarkMode ? "bg-gray-400" : "bg-gray-500"
                      : isDarkMode ? "bg-gray-700" : "bg-gray-300"
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={handleSkip}
              className={`text-sm ${getSecondaryText(isDarkMode)} hover:opacity-70 transition-opacity`}
            >
              Skip
            </button>

            <div className="flex gap-2">
              {currentStep > 0 && (
                <button
                  onClick={handlePrevious}
                  className={`px-4 py-2 rounded-lg border ${getLightBorder(isDarkMode)} ${getSecondaryText(isDarkMode)} hover:opacity-80 transition-opacity text-sm font-medium`}
                >
                  Previous
                </button>
              )}
              <button
                onClick={handleNext}
                className="px-4 py-2 rounded-lg hover:opacity-90 transition-opacity text-sm font-medium bg-[var(--cc-action)] text-[var(--cc-action-text)]"
              >
                {currentStep < steps.length - 1 ? 'Next' : 'Finish'}
              </button>
            </div>
          </div>

          {/* Step counter */}
          <div className={`mt-4 text-center text-xs ${getSecondaryText(isDarkMode)}`}>
            Step {currentStep + 1} of {steps.length}
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingWalkthrough;
